import React, { useState, useEffect } from 'react'
import { Head, Link, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../Shared/Layout/LayoutAdmin'
import { Card, Table, Form, Row, Col, Modal, Spinner } from 'react-bootstrap'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import AlertDismissible from '../../Shared/Components/AlertDismissible'
import { formatMoney } from '../../utils/formatters';

const AdviserMetrics = ({ adviser, metrics }) => {
    const { flash } = usePage().props
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: ''
    });
    const [selectedStatus, setSelectedStatus] = useState('');
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedMetricId, setSelectedMetricId] = useState(null);

    const statusOptions = [
        { value: '', label: 'Todos' },
        { value: 'pending', label: 'Pendiente' },
        { value: 'paid', label: 'Pagado' },
        { value: 'cancelled', label: 'Cancelado' }
    ];

    // Efecto para mostrar mensajes flash
    useEffect(() => {
        if (flash?.success) {
            setAlertMessage(flash.success);
            setAlertType('success');
            setShowAlert(true);
            setShowLoadingModal(false); // Cerrar modal de loading
        }
        if (flash?.error) {
            setAlertMessage(flash.error);
            setAlertType('danger');
            setShowAlert(true);
            setShowLoadingModal(false); // Cerrar modal de loading
        }
    }, [flash]);

    const handleFilter = () => {
        Inertia.get(route('commission-metrics.adviser', adviser.id), {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            status: selectedStatus
        }, {
            preserveState: true
        });
    };

    // Función para mostrar el modal de confirmación
    const handleConfirmSendCfdi = (metricId) => {
        setSelectedMetricId(metricId);
        setShowConfirmModal(true);
    };

    // Función para enviar CFDI
    const handleSendCfdi = () => {
        setShowConfirmModal(false);
        // Mostrar modal de loading
        setLoadingMessage('Enviando CFDI...');
        setShowLoadingModal(true);
        setShowAlert(false);

        Inertia.post(route('commission-metrics.send-cfdi', selectedMetricId), {}, {
            preserveScroll: true,
            onSuccess: () => {
                console.log('CFDI enviado correctamente');
            },
            onError: (errors) => {
                console.error('Error al enviar CFDI:', errors);
                setShowLoadingModal(false);
            }
        });
    };

    // Función para descargar CFDI
    const handleDownloadCfdi = (metricId) => {
        setLoadingMessage('Descargando factura...');
        setShowLoadingModal(true);
        setShowAlert(false);

        // Simular el proceso de descarga
        window.location.href = route('commission-metrics.download-cfdi', metricId);

        // Cerrar el modal después de 2 segundos (tiempo estimado para iniciar la descarga)
        setTimeout(() => {
            setShowLoadingModal(false);
        }, 2000);
    };

    // Función para formatear el mes en español
    const formatPeriod = (year, month, day) => {
        const months = [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];
        const safeDay = day || 1;
        return `${safeDay} ${months[month - 1]} ${year}`;
    };

    return (
        <>
            <Head title={`Comisiones de ${adviser.user.name}`} />

            <div className="mb-7 mt-7">
                <Breadcrumbs currentPath={`/admin/commission-metrics/adviser/${adviser.id}`} />
            </div>

            {showAlert && (
                <AlertDismissible
                    message={alertMessage}
                    type={alertType}
                    onClose={() => setShowAlert(false)}
                />
            )}

            {/* Modal de Confirmación */}
            <Modal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Generación de Factura</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>¿Está seguro que desea generar la factura para esta métrica?</p>
                    <p className="text-muted mb-0">
                        <small>Esta acción generará un CFDI y cambiará el estado de la métrica a "Pagado".</small>
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowConfirmModal(false)}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSendCfdi}
                    >
                        <i className="bi bi-check-circle me-1"></i>
                        Confirmar
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Loading */}
            <Modal
                show={showLoadingModal}
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <h5 className="mb-2">{loadingMessage}</h5>
                    <p className="text-muted mb-0">Por favor espere mientras procesamos su solicitud</p>
                </Modal.Body>
            </Modal>

            <div className="card card-flush mb-7">
                <div className="card-header">
                    <h3 className="card-title">Comisiones de {adviser.user.name} {adviser.lastname_ap} {adviser.lastname_am}</h3>
                </div>
                <div className="card-body">
                    <Row className="align-items-end">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Fecha Inicial</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateRange.startDate}
                                    onChange={(e) => setDateRange(prev => ({
                                        ...prev,
                                        startDate: e.target.value
                                    }))}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Fecha Final</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={dateRange.endDate}
                                    onChange={(e) => setDateRange(prev => ({
                                        ...prev,
                                        endDate: e.target.value
                                    }))}
                                    min={dateRange.startDate}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Estatus</Form.Label>
                                <Form.Select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleFilter}
                                disabled={!dateRange.startDate || !dateRange.endDate}
                            >
                                Aplicar Filtros
                            </button>
                        </Col>
                    </Row>
                </div>
            </div>

            <div className="card card-flush h-md-100">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-row-dashed align-middle gs-0 gy-3 my-0">
                            <thead>
                                <tr className="fs-7 fw-bold text-gray-400 border-bottom-0">
                                    <th className="p-0 pb-3 min-w-175px text-start">PERÍODO</th>
                                    <th className="p-0 pb-3 min-w-125px text-start">TOTAL PRIMA</th>
                                    <th className="p-0 pb-3 min-w-100px text-start">TOTAL PÓLIZAS</th>
                                    <th className="p-0 pb-3 min-w-100px text-start">ESTADO</th>
                                    <th className="p-0 pb-3">ACCIONES</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.data.filter(metric => {
                                    const metricDate = new Date(metric.year, metric.month - 1, metric.day || 1);
                                    const startDate = new Date(dateRange.startDate);
                                    const endDate = new Date(dateRange.endDate);

                                    return (!dateRange.startDate || metricDate >= startDate) &&
                                        (!dateRange.endDate || metricDate <= endDate) &&
                                        (!selectedStatus || metric.status === selectedStatus);
                                }).map(metric => (
                                    <tr key={metric.id}>
                                        <td className="fw-bold">{formatPeriod(metric.year, metric.month, metric.day)}</td>
                                        <td>{formatMoney(metric.total_bonus)}</td>
                                        <td>{metric.commission_metrics_policies.length}</td>
                                        <td>
                                            <span className={`badge ${metric.status === 'pending' ? 'badge-warning' :
                                                metric.status === 'paid' ? 'badge-success' : 'badge-danger'}`}>
                                                {metric.status === 'pending' ? 'Pendiente' :
                                                    metric.status === 'paid' ? 'Pagado' : 'Cancelado'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-around">
                                                <div className='mx-2'>
                                                    <Link href={route('commission-metrics.show', metric.id)}>
                                                        Ver Detalle
                                                    </Link>
                                                </div>
                                                <div className='mx-2'>
                                                    <Link href={route('commission-metrics.cancelled', metric.id)}>
                                                        Pólizas Canceladas
                                                    </Link>
                                                </div>
                                                <div className='mx-2'>
                                                    {metric.invoice_commission_inbursa?.success && metric.invoice_commission_inbursa?.uuid ? (
                                                        <button
                                                            className="btn btn-link p-0 text-success"
                                                            onClick={() => handleDownloadCfdi(metric.id)}
                                                        >
                                                            <i className="bi bi-download me-1"></i>
                                                            Descargar Factura
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-link p-0"
                                                            onClick={() => handleConfirmSendCfdi(metric.id)}
                                                            disabled={showLoadingModal}
                                                        >
                                                            <i className="bi bi-receipt me-1"></i>
                                                            Generar Factura
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    )
}

AdviserMetrics.layout = page => <Layout children={page} title="Comisiones por Asesor" />

export default AdviserMetrics
